import React, { useState, useEffect, useRef } from 'react';
import { format, parse } from 'date-fns';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const CustomDateInput = ({ 
  name, 
  id,
  value,
  onChange,
  className = "form-control", 
  disabled = false,
  placeholder = "DD/MM/YYYY"
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [mysqlValue, setMysqlValue] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const inputRef = useRef(null);
  const calendarRef = useRef(null);
  const hiddenInputRef = useRef(null);

  // Extract actual date value from either string or object format
  const extractValue = (val) => {
    if (!val) return '';
    
    let dateValue = '';
    
    // If it's an object with target.value structure
    if (typeof val === 'object' && val.target && val.target.value) {
      dateValue = val.target.value;
    }
    // If it's a string
    else if (typeof val === 'string') {
      dateValue = val;
    }
    
    // Check for invalid date values like "0000-00-00" or empty strings
    if (!dateValue || dateValue === '0000-00-00' || dateValue.trim() === '') {
      return '';
    }
    
    // Additional validation - check if it's a valid date
    try {
      const testDate = new Date(dateValue);
      if (isNaN(testDate.getTime()) || testDate.getFullYear() < 1900) {
        return '';
      }
    } catch (error) {
      return '';
    }
    
    return dateValue;
  };

  // Convert API format (YYYY-MM-DD) to display format (DD/MM/YYYY)
  const formatForDisplay = (dateValue) => {
    if (!dateValue || dateValue === '0000-00-00') return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '';
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      return '';
    }
  };

  // Convert display format (DD/MM/YYYY) to API format (YYYY-MM-DD)
  const formatForAPI = (displayValue) => {
    if (!displayValue) return '';
    try {
      const parsedDate = parse(displayValue, 'dd/MM/yyyy', new Date());
      if (isNaN(parsedDate.getTime())) return '';
      return format(parsedDate, 'yyyy-MM-dd');
    } catch (error) {
      return '';
    }
  };

  // Initialize values when component mounts or value prop changes
  useEffect(() => {
    const actualValue = extractValue(value);
    
    if (actualValue) {
      const display = formatForDisplay(actualValue);
      const mysql = actualValue.includes('-') ? actualValue : formatForAPI(actualValue);
      
      setDisplayValue(display);
      setMysqlValue(mysql);
      
      // Update hidden input directly
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = mysql;
      }
    } else {
      // Clear values if no value provided
      setDisplayValue('');
      setMysqlValue('');
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = '';
      }
    }
  }, [value]);

  // Add this useEffect to ensure hidden input always has the correct value
  useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = mysqlValue;
    }
  }, [mysqlValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputClick = () => {
    if (!disabled) {
      setShowCalendar(!showCalendar);
    }
  };

  const handleDateSelect = (date) => {
    const apiValue = format(date, 'yyyy-MM-dd');
    const displayValue = format(date, 'dd/MM/yyyy');
    
    setDisplayValue(displayValue);
    setMysqlValue(apiValue);
    setShowCalendar(false);
    
    // Call onChange if provided (for controlled components)
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: apiValue
        }
      });
    }
  };

  // Get the current date value for the calendar
  const getCalendarValue = () => {
    if (!mysqlValue) return null;
    
    try {
      return new Date(mysqlValue);
    } catch (error) {
      return null;
    }
  };

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
        {/* Display input - shows DD/MM/YYYY format */}
        <input
          ref={inputRef}
          type="text"
          id={`${id}_display`} // Change ID to avoid conflicts
          value={displayValue}
          onChange={() => {}} // No-op function since input is read-only
          onClick={handleInputClick}
          className={className}
          disabled={disabled}
          readOnly
          placeholder={placeholder}
          style={{ 
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#f5f5f5' : '#fff'
          }}
        />
        
        {/* Hidden input - holds YYYY-MM-DD format for form submission */}
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={name}
          id={id} // Keep the original ID for form submission
          value={mysqlValue}
        />
        
        {showCalendar && (
          <div
            ref={calendarRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              zIndex: 1000,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          >
            <Calendar
              onChange={handleDateSelect}
              value={getCalendarValue()}
              maxDetail="month"
              minDetail="month"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CustomDateInput;